/**
 * 아이디 찾기, 비밀번호 찾기 (공통/보안과개인정보 1-4)
 * 사무소 이메일로 인증번호를 보내고, 남은 시간을 09:59부터 1초마다 줄여 보여 준다.
 */
import { useState } from 'react';
import { api } from '../api.ts';
import { Button, Countdown, Field, Link, Message, Page } from '../ui.tsx';

interface Sent {
  code_id: string;
  seconds: number;
  at: number;
  email: string;
  test_code?: string;
}

function CodeBox(props: { sent: Sent; code: string; setCode: (v: string) => void; onCheck: () => void }) {
  const [expired, setExpired] = useState(false);
  return (
    <div>
      <Message text={`인증번호를 보냈어요. (${props.sent.email})`} kind="info" />
      {props.sent.test_code && <p>시험용 인증번호: {props.sent.test_code}</p>}
      {expired
        ? <Message text="시간이 지났어요. 인증번호를 다시 받아 주세요." />
        : <Countdown seconds={props.sent.seconds} startedAt={props.sent.at} onEnd={() => setExpired(true)} />}
      <Field label="인증번호" value={props.code} onChange={props.setCode} type="tel" />
      <Button primary disabled={expired} onClick={props.onCheck}>확인</Button>
    </div>
  );
}

export function FindId({ go }: { go: (p: string) => void }) {
  const [biz_no, setBiz] = useState('');
  const [sent, setSent] = useState<Sent | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<string | null>(null);

  async function send() {
    setError(null);
    const r = await api<{ code_id: string; seconds: number; email: string; test_code?: string }>('POST', '/api/find/id/send', { biz_no });
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setSent({ code_id: r.code_id, seconds: r.seconds, at: Date.now(), email: r.email, ...(r.test_code ? { test_code: r.test_code } : {}) });
  }

  async function check() {
    if (!sent) return;
    const r = await api<{ login_id: string | null; has_kakao: boolean; has_naver: boolean }>('POST', '/api/find/id/check', { code_id: sent.code_id, code, biz_no });
    if (!r.ok) {
      setError(r.message);
      return;
    }
    const ways = [r.login_id ? `아이디: ${r.login_id}` : null, r.has_kakao ? '카카오로 가입' : null, r.has_naver ? '네이버로 가입' : null].filter(Boolean);
    setFound(ways.join(', '));
  }

  return (
    <Page title="아이디 찾기">
      {found ? <Message text={found} kind="info" /> : (
        <>
          <Field label="사업자등록번호" value={biz_no} onChange={setBiz} type="tel" />
          <Button onClick={() => void send()}>{sent ? '인증번호 다시 받기' : '인증번호 받기'}</Button>
          {sent && <CodeBox sent={sent} code={code} setCode={setCode} onCheck={() => void check()} />}
        </>
      )}
      <Message text={error} />
      <p><Link to="/login" go={go}>로그인으로 돌아가기</Link></p>
    </Page>
  );
}

export function FindPassword({ go }: { go: (p: string) => void }) {
  const [login_id, setId] = useState('');
  const [sent, setSent] = useState<Sent | null>(null);
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function send() {
    setError(null);
    const r = await api<{ code_id: string; seconds: number; email: string; test_code?: string }>('POST', '/api/find/password/send', { login_id });
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setSent({ code_id: r.code_id, seconds: r.seconds, at: Date.now(), email: r.email, ...(r.test_code ? { test_code: r.test_code } : {}) });
  }

  async function check() {
    if (!sent) return;
    const r = await api('POST', '/api/find/password/check', { code_id: sent.code_id, code });
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setError(null);
    setVerified(true);
  }

  async function reset() {
    if (!sent) return;
    const r = await api<{ message: string }>('POST', '/api/find/password/reset', { code_id: sent.code_id, password });
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setDone(r.message);
  }

  return (
    <Page title="비밀번호 찾기">
      {done ? <Message text={done} kind="info" /> : verified ? (
        <>
          <Field label="새 비밀번호" value={password} onChange={setPassword} type="password" autoComplete="new-password" />
          <Button primary onClick={() => void reset()}>새 비밀번호로 바꾸기</Button>
        </>
      ) : (
        <>
          <Field label="아이디" value={login_id} onChange={setId} autoComplete="username" />
          <Button onClick={() => void send()}>{sent ? '인증번호 다시 받기' : '인증번호 받기'}</Button>
          {sent && <CodeBox sent={sent} code={code} setCode={setCode} onCheck={() => void check()} />}
        </>
      )}
      <Message text={error} />
      <p><Link to="/login" go={go}>로그인으로 돌아가기</Link></p>
    </Page>
  );
}
